// Censo de dietas terapéuticas y su operación. Catering → Operación → Censo de dietas.
// (1) Censo del día (prescripción: cuántos de cada dieta por contrato/servicio),
// (2) Plan del día para la cocina (qué producir por dieta, con validación nutricional),
// (3) Etiquetas por bandeja (imprimibles), (4) Menú por dieta (override del general).
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Utensils, Tags, ListChecks, Printer, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls, num } from '@/features/inventario/shared';
import { cat, catRpc, invalidateCatering, SERVICIO_LABEL } from './shared';

interface Dieta {
  id: string; codigo: string | null; nombre: string; es_terapeutica: boolean;
  energia_min: number | null; energia_max: number | null; proteina_min: number | null; proteina_max: number | null;
  grasa_max: number | null; cho_max: number | null; sodio_max: number | null; es_activo: boolean;
}
interface CensoRow {
  contrato_id: string; contrato_codigo: string; cliente: string; servicio: string;
  pax: number; terapeuticas: number; dietas: { dieta_tipo_id: string; dieta: string; cantidad: number }[];
}
interface Aporte { energia_kcal: number; proteina_g: number; grasa_g: number; carbohidrato_g: number; fibra_g: number; sodio_mg: number }
interface PlanRow { servicio: string; dieta: string; dieta_tipo_id: string; cantidad: number; menu: string[]; aporte: Aporte }
interface Etiqueta { cliente: string; contrato: string; servicio: string; dieta: string; cantidad: number; menu: string[]; alergenos: string[]; aporte: { energia_kcal: number; proteina_g: number; sodio_mg: number } }
interface Ciclo { id: string; nombre: string }
interface Receta { id: string; nombre: string; tipo: string }

const isoHoy = (): string => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const DIAS = ['', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

// Semáforo cliente: aporte vs objetivos de la dieta
function estado(v: number, min: number | null, max: number | null): 'ok' | 'alto' | 'bajo' | 'na' {
  if (min == null && max == null) return 'na';
  if (max != null && v > max) return 'alto';
  if (min != null && v < min) return 'bajo';
  return 'ok';
}
const CLS: Record<string, string> = { ok: 'text-green-700 dark:text-green-400', alto: 'text-red-600 font-medium', bajo: 'text-amber-600 font-medium', na: 'text-muted-foreground' };

export function DietasCatering() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'censo' | 'plan' | 'etiquetas' | 'menu'>('censo');
  const [fecha, setFecha] = useState(isoHoy());
  const { data: dietas = [] } = useQuery<Dieta[]>({ queryKey: ['cat-dietas'], queryFn: () => catRpc<Dieta[]>('cat_dieta_listar') });
  const terapeuticas = useMemo(() => dietas.filter((d) => d.es_terapeutica && d.es_activo), [dietas]);

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-center gap-2">
        {([['censo', 'Censo del día', ClipboardList], ['plan', 'Plan de cocina', Utensils], ['etiquetas', 'Etiquetas', Tags], ['menu', 'Menú por dieta', ListChecks]] as const).map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm ${tab === k ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
        {tab !== 'menu' && (
          <div className="ml-auto flex items-end gap-2">
            <div><label className="block text-xs text-muted-foreground">Fecha</label><input type="date" className={`${fieldCls} w-40`} value={fecha} onChange={(e) => setFecha(e.target.value)} /></div>
          </div>
        )}
      </div>

      {tab === 'censo' && <TabCenso qc={qc} fecha={fecha} terapeuticas={terapeuticas} />}
      {tab === 'plan' && <TabPlan fecha={fecha} dietas={dietas} />}
      {tab === 'etiquetas' && <TabEtiquetas fecha={fecha} />}
      {tab === 'menu' && <TabMenu terapeuticas={terapeuticas} />}
    </div>
  );
}

function TabCenso({ qc, fecha, terapeuticas }: { qc: ReturnType<typeof useQueryClient>; fecha: string; terapeuticas: Dieta[] }) {
  const { data: censo = [] } = useQuery<CensoRow[]>({ queryKey: ['cat-censo', fecha], queryFn: () => catRpc<CensoRow[]>('cat_dieta_conteo_get', { _fecha: fecha }) });
  const [edit, setEdit] = useState<string | null>(null);        // clave contrato|servicio
  const [vals, setVals] = useState<Record<string, string>>({}); // dieta_tipo_id -> cantidad

  const guardar = useMutation({
    mutationFn: (r: CensoRow) => catRpc('cat_dieta_conteo_set', {
      _contrato_id: r.contrato_id, _fecha: fecha,
      _lineas: terapeuticas.map((d) => ({ servicio: r.servicio, dieta_tipo_id: d.id, cantidad: parseFloat(vals[d.id] || '0') || 0 })),
    }),
    onSuccess: () => { toast.success('Censo guardado'); setEdit(null); invalidateCatering(qc); void qc.invalidateQueries({ queryKey: ['cat-censo', fecha] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const abrir = (r: CensoRow) => {
    setEdit(`${r.contrato_id}|${r.servicio}`);
    const v: Record<string, string> = {};
    terapeuticas.forEach((d) => { const found = r.dietas.find((x) => x.dieta_tipo_id === d.id); v[d.id] = found ? String(found.cantidad) : ''; });
    setVals(v);
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Censo de dietas — {fecha}</CardTitle>
        <p className="pt-1 text-xs text-muted-foreground">Registra cuántos comensales de cada dieta terapéutica hay por contrato y servicio. El resto es dieta general. Debe hacerse ANTES de generar la orden de producción del día.</p>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {!terapeuticas.length && <p className="text-sm text-amber-600">No hay dietas terapéuticas definidas. Créalas en Nutrición y dietas → Tipos de dieta.</p>}
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-1 pr-2">Contrato</th><th className="py-1 pr-2">Servicio</th>
            <th className="py-1 pr-2 text-right">Pax</th><th className="py-1 pr-2 text-right">General</th>
            <th className="py-1 pr-2">Dietas terapéuticas</th><th />
          </tr></thead>
          <tbody>
            {censo.map((r) => {
              const key = `${r.contrato_id}|${r.servicio}`;
              return (
                <tr key={key} className="border-b align-top last:border-0">
                  <td className="py-1.5 pr-2">{r.cliente}<span className="block text-xs text-muted-foreground">{r.contrato_codigo}</span></td>
                  <td className="py-1.5 pr-2">{SERVICIO_LABEL[r.servicio] ?? r.servicio}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{num(r.pax, 0)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{num(r.pax - r.terapeuticas, 0)}</td>
                  <td className="py-1.5 pr-2">
                    {edit === key ? (
                      <div className="flex flex-wrap gap-2">
                        {terapeuticas.map((d) => (
                          <label key={d.id} className="flex items-center gap-1 text-xs">{d.nombre}
                            <input className={`${fieldCls} w-16`} type="number" min="0" value={vals[d.id] ?? ''} onChange={(e) => setVals((s) => ({ ...s, [d.id]: e.target.value }))} /></label>
                        ))}
                        <Button size="sm" disabled={guardar.isPending} onClick={() => guardar.mutate(r)}>Guardar</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEdit(null)}>Cancelar</Button>
                      </div>
                    ) : r.dietas.length ? r.dietas.map((x) => `${x.dieta} ${num(x.cantidad, 0)}`).join(' · ') : <span className="text-xs text-muted-foreground">solo general</span>}
                  </td>
                  <td className="py-1.5 text-right">{edit !== key && <button className="text-xs text-primary underline" onClick={() => abrir(r)}>editar</button>}</td>
                </tr>
              );
            })}
            {!censo.length && <tr><td colSpan={6} className="py-2 text-sm text-muted-foreground">No hay efectivos (pax) para esta fecha. Regístralos en Contratos y pax.</td></tr>}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function TabPlan({ fecha, dietas }: { fecha: string; dietas: Dieta[] }) {
  const { data: plan = [] } = useQuery<PlanRow[]>({ queryKey: ['cat-dietas-dia', fecha], queryFn: () => catRpc<PlanRow[]>('cat_dietas_del_dia', { _fecha: fecha }) });
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Plan de dietas para la cocina — {fecha}</CardTitle>
        <p className="pt-1 text-xs text-muted-foreground">Cuántas dietas terapéuticas producir, su menú resuelto y si el aporte cumple el objetivo de la dieta (▲ se pasa · ▼ falta).</p></CardHeader>
      <CardContent className="space-y-3">
        {plan.map((p, i) => {
          const d = dietas.find((x) => x.id === p.dieta_tipo_id);
          const a = p.aporte;
          const chips = d ? [
            ['kcal', a.energia_kcal, estado(a.energia_kcal, d.energia_min, d.energia_max)],
            ['prot', a.proteina_g, estado(a.proteina_g, d.proteina_min, d.proteina_max)],
            ['grasa', a.grasa_g, estado(a.grasa_g, null, d.grasa_max)],
            ['CHO', a.carbohidrato_g, estado(a.carbohidrato_g, null, d.cho_max)],
            ['Na', a.sodio_mg, estado(a.sodio_mg, null, d.sodio_max)],
          ] as const : [];
          return (
            <div key={i} className="rounded-md border p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-medium">{SERVICIO_LABEL[p.servicio] ?? p.servicio} · {p.dieta} <span className="rounded-full bg-primary/10 px-2 text-sm text-primary">×{num(p.cantidad, 0)}</span></p>
                <div className="flex flex-wrap gap-2 text-xs">
                  {chips.map(([lbl, val, st]) => <span key={lbl} className={CLS[st]}>{lbl} {num(val as number, lbl === 'kcal' || lbl === 'Na' ? 0 : 1)} {st === 'alto' ? '▲' : st === 'bajo' ? '▼' : st === 'ok' ? '✓' : ''}</span>)}
                </div>
              </div>
              <p className="pt-1 text-xs text-muted-foreground">Menú: {p.menu.join(', ')}</p>
            </div>
          );
        })}
        {!plan.length && <p className="text-sm text-muted-foreground">No hay dietas terapéuticas en el censo de esta fecha.</p>}
      </CardContent>
    </Card>
  );
}

function TabEtiquetas({ fecha }: { fecha: string }) {
  const { data: etiquetas = [] } = useQuery<Etiqueta[]>({ queryKey: ['cat-etiquetas', fecha], queryFn: () => catRpc<Etiqueta[]>('cat_dieta_etiquetas', { _fecha: fecha }) });
  const total = etiquetas.reduce((s, e) => s + Number(e.cantidad || 0), 0);
  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div><CardTitle className="text-base">Etiquetas por bandeja — {fecha}</CardTitle>
          <p className="pt-1 text-xs text-muted-foreground">{num(total, 0)} bandejas terapéuticas. Cada tarjeta identifica la dieta, el menú, los alérgenos y el aporte.</p></div>
        {!!etiquetas.length && <Button size="sm" variant="outline" onClick={() => window.print()}><Printer className="mr-1 h-3.5 w-3.5" /> Imprimir</Button>}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {etiquetas.map((e, i) => (
            <div key={i} className="rounded-md border p-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">{e.dieta}</span>
                <span className="text-xs text-muted-foreground">×{num(e.cantidad, 0)}</span>
              </div>
              <p className="pt-1 font-medium">{e.cliente}</p>
              <p className="text-xs text-muted-foreground">{SERVICIO_LABEL[e.servicio] ?? e.servicio}</p>
              <p className="pt-1 text-xs">{e.menu.join(', ')}</p>
              <p className="pt-1 text-xs text-muted-foreground">{e.aporte.energia_kcal} kcal · prot {e.aporte.proteina_g}g · Na {e.aporte.sodio_mg}mg</p>
              {e.alergenos.length > 0 && <p className="pt-0.5 text-xs font-medium text-red-600">Alérgenos: {e.alergenos.join(', ')}</p>}
            </div>
          ))}
          {!etiquetas.length && <p className="text-sm text-muted-foreground">No hay dietas terapéuticas en el censo de esta fecha.</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function TabMenu({ terapeuticas }: { terapeuticas: Dieta[] }) {
  const qc = useQueryClient();
  const [dietaId, setDietaId] = useState('');
  const [cicloId, setCicloId] = useState('');
  const [dia, setDia] = useState('1');
  const [servicio, setServicio] = useState('almuerzo_1');
  const [sel, setSel] = useState<string[]>([]);
  const { data: ciclos = [] } = useQuery<Ciclo[]>({ queryKey: ['cat-ciclos-opt'], queryFn: () => cat.list<Ciclo>('ciclos', '?select=id,nombre&deleted_at=is.null&order=orden') });
  const { data: recetas = [] } = useQuery<Receta[]>({ queryKey: ['cat-recetas-opt'], queryFn: () => catRpc<Receta[]>('recetas_listar', { _tipo: null }) });
  const platos = useMemo(() => recetas.filter((r) => r.tipo === 'plato'), [recetas]);
  const puede = dietaId && cicloId;
  const { data: override = [] } = useQuery<{ id: string; dia: number; servicio: string; receta_id: string; receta: string }[]>({
    queryKey: ['cat-dieta-menu', dietaId, cicloId], enabled: !!puede,
    queryFn: () => catRpc('cat_dieta_menu_get', { _dieta_tipo_id: dietaId, _ciclo_id: cicloId }),
  });

  const guardar = useMutation({
    mutationFn: () => catRpc('cat_dieta_menu_set', { _dieta_tipo_id: dietaId, _ciclo_id: cicloId, _dia: parseInt(dia, 10), _servicio: servicio, _recetas: sel }),
    onSuccess: () => { toast.success('Menú de la dieta guardado'); setSel([]); void qc.invalidateQueries({ queryKey: ['cat-dieta-menu', dietaId, cicloId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Menú por dieta (override del general)</CardTitle>
        <p className="pt-1 text-xs text-muted-foreground">Si una dieta no define su menú para un día/servicio, hereda el menú general del ciclo. Aquí defines la sustitución (p. ej. renal sin sal): la orden de producción producirá esas recetas para esos comensales.</p></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <div><label className="block text-xs text-muted-foreground">Dieta</label>
            <select className={`${fieldCls} w-44`} value={dietaId} onChange={(e) => setDietaId(e.target.value)}><option value="">…</option>{terapeuticas.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}</select></div>
          <div><label className="block text-xs text-muted-foreground">Ciclo</label>
            <select className={`${fieldCls} w-40`} value={cicloId} onChange={(e) => setCicloId(e.target.value)}><option value="">…</option>{ciclos.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}</select></div>
          <div><label className="block text-xs text-muted-foreground">Día</label>
            <select className={`${fieldCls} w-32`} value={dia} onChange={(e) => setDia(e.target.value)}>{[1,2,3,4,5,6,7].map((n) => <option key={n} value={n}>{DIAS[n]}</option>)}</select></div>
          <div><label className="block text-xs text-muted-foreground">Servicio</label>
            <select className={`${fieldCls} w-36`} value={servicio} onChange={(e) => setServicio(e.target.value)}>{Object.entries(SERVICIO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
        </div>

        {!puede ? <p className="text-sm text-muted-foreground">Elige dieta y ciclo.</p> : (
          <>
            <div>
              <p className="pb-1 text-sm font-medium">Recetas del menú de esta dieta (elige las que reemplazan al general)</p>
              <div className="grid max-h-64 grid-cols-1 gap-1 overflow-y-auto sm:grid-cols-2">
                {platos.map((r) => (
                  <label key={r.id} className="flex items-center gap-2 rounded-md border px-2 py-1 text-sm">
                    <input type="checkbox" checked={sel.includes(r.id)} onChange={(e) => setSel((s) => e.target.checked ? [...s, r.id] : s.filter((x) => x !== r.id))} /> {r.nombre}
                  </label>
                ))}
              </div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" disabled={!sel.length || guardar.isPending} onClick={() => guardar.mutate()}><Plus className="mr-1 h-3.5 w-3.5" /> Fijar menú ({sel.length})</Button>
                <span className="self-center text-xs text-muted-foreground">Guardar con 0 recetas no borra; para volver al general, elimina el override (próxima iteración).</span>
              </div>
            </div>
            <div>
              <p className="pb-1 text-sm font-medium">Overrides definidos para esta dieta y ciclo</p>
              {override.length ? (
                <ul className="space-y-0.5 text-xs">
                  {override.map((o) => <li key={o.id}>{DIAS[o.dia]} · {SERVICIO_LABEL[o.servicio] ?? o.servicio}: <b>{o.receta}</b></li>)}
                </ul>
              ) : <p className="text-xs text-muted-foreground">Sin overrides: esta dieta hereda el menú general en todo el ciclo.</p>}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
